import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  closeOnOverlayClick?: boolean;
  closeOnEscapeKey?: boolean;
  showCloseButton?: boolean;
  preventClose?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscapeKey = true,
  showCloseButton = true,
  preventClose = false,
  className = '',
  overlayClassName = '',
  contentClassName = '',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  const sizeClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
  };

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscapeKey || preventClose) return;

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen, closeOnEscapeKey, onClose, preventClose]);

  // Handle focus management
  useEffect(() => {
    if (!isOpen) return;

    // Store current focused element
    previousFocusRef.current = document.activeElement as HTMLElement;

    // Focus modal when it opens
    if (modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstFocusable = focusableElements[0] as HTMLElement;
      firstFocusable?.focus();
    }

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      // Restore body scroll
      document.body.style.overflow = 'unset';

      // Restore focus when modal closes
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
      }
    };
  }, [isOpen]);

  // Handle overlay click
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (
      closeOnOverlayClick &&
      !preventClose &&
      event.target === event.currentTarget
    ) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${overlayClassName}`}
      onClick={handleOverlayClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 backdrop-blur-sm" style={{ backgroundColor: 'rgba(5, 45, 73, 0.5)' }} />

      {/* Modal Content */}
      <div
        ref={modalRef}
        className={`
          relative w-full transform transition-all duration-300 ease-out
          ${sizeClasses[size]}
          ${className}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`relative rounded-xl ${contentClassName}`}
             style={{
               background: 'var(--patreon-card-bg)',
               boxShadow: 'var(--patreon-shadow-xl)'
             }}>
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: 'var(--patreon-card-border)' }}>
              <div className="flex-1">
                {title && (
                  <h2 className="text-xl font-semibold" style={{ color: 'var(--patreon-black)' }}>
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="mt-1 text-sm" style={{ color: 'var(--patreon-gray)' }}>
                    {description}
                  </p>
                )}
              </div>
              {showCloseButton && !preventClose && (
                <button
                  onClick={onClose}
                  className="ml-4 p-2 rounded-lg transition-colors"
                  style={{ color: 'var(--patreon-gray)' }}
                  aria-label="Close modal"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              )}
            </div>
          )}

          {/* Body */}
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal outside of component tree
  return createPortal(modalContent, document.body);
};

// Modal sub-components for better structure
interface ModalHeaderProps {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  className?: string;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  description,
  children,
  className = '',
}) => {
  return (
    <div className={`p-6 border-b ${className}`} style={{ borderColor: 'var(--patreon-card-border)' }}>
      {title && (
        <h2 className="text-xl font-semibold" style={{ color: 'var(--patreon-black)' }}>
          {title}
        </h2>
      )}
      {description && (
        <p className="mt-1 text-sm" style={{ color: 'var(--patreon-gray)' }}>
          {description}
        </p>
      )}
      {children}
    </div>
  );
};

interface ModalContentProps {
  children: React.ReactNode;
  className?: string;
}

const ModalContent: React.FC<ModalContentProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`p-6 ${className}`}>
      {children}
    </div>
  );
};

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
}

const ModalFooter: React.FC<ModalFooterProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`p-6 border-t ${className}`} style={{ borderColor: 'var(--patreon-card-border)' }}>
      <div className="flex justify-end space-x-3">
        {children}
      </div>
    </div>
  );
};

export { Modal, ModalHeader, ModalContent, ModalFooter };
export default Modal;
export type { ModalProps, ModalHeaderProps, ModalContentProps, ModalFooterProps };
